import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  type CardProps,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
} from '@chakra-ui/react';
import { stringify } from 'flatted';
import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  ActionModal,
  FeatureTrackingEvents,
  getPlotColorLookup,
} from '@revelio/core';

type PlotLinkModalStateType = {
  isPlotLinkOpen: boolean;
  onPlotLinkOpen: () => void;
  onPlotLinkClose: () => void;
};

export interface PlotLinkModalProps {
  plotLinkModalState: PlotLinkModalStateType;
  cardConfig?: Partial<CardProps> & { header?: string };
  typeAndProps?: unknown;
  data: unknown;
  filters?: Record<string, unknown>;
}

export function PlotLinkModal({
  plotLinkModalState,
  cardConfig,
  typeAndProps,
  data,
  filters = {},
}: PlotLinkModalProps) {
  const { isPlotLinkOpen: isOpen, onPlotLinkClose: onClose } =
    plotLinkModalState;

  const { loggedInUser } = useGetLoggedInUser();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  const dataObject: any = {
    plotData: data,
    cardConfig: cardConfig,
    typeAndProps: typeAndProps,
    userID: loggedInUser.id,
    colors: getPlotColorLookup(),
    filters,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [checkData, setCheckData] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setCheckData('');
  }, [
    plotLinkModalState.isPlotLinkOpen,
    dataObject.cardConfig.endpointSegment,
    dataObject.cardConfig.header,
    dataObject.cardConfig.view,
    dataObject.cardConfig.viewType,
    dataObject.plotData?.length,
    dataObject.plotData?.links?.length,
    dataObject.plotData?.nodes?.length,
  ]);

  const createPlotLink = async () => {
    mixpanel.track(FeatureTrackingEvents.PLOT_LINK_GENERATE, {
      plot_title: cardConfig?.header,
    });

    setIsLoading(true);
    delete dataObject.cardConfig.footer;
    const finalObject = {
      meta: {
        userID: dataObject.userID,
        ...dataObject.cardConfig,
      },
      config: {
        ...dataObject.typeAndProps.chartProps,
        colorLookup: dataObject.colors,
      },
      type: dataObject.typeAndProps.chartType,
      data: Array.isArray(dataObject.plotData)
        ? dataObject.plotData
        : stringify(dataObject.plotData),
      filters: dataObject.filters,
    };
    try {
      const response = await fetch(
        'https://us-central1-dashboard-ui-revelio.cloudfunctions.net/plotShare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(finalObject),
        }
      );
      const jsonData = await response.json();
      setCheckData(
        jsonData.id
          ? `https://charts.reveliolabs.com/dashboard/${jsonData.id}`
          : ''
      );
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  };

  const [copiedGeneratedPlotLink, setCopiedGeneratedPlotLink] = useState(false);
  const copyTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutIdRef.current) {
        clearTimeout(copyTimeoutIdRef.current);
      }
    };
  }, []);

  const handleCopyGeneratedPlotLink = () => {
    if (copyTimeoutIdRef.current) {
      clearTimeout(copyTimeoutIdRef.current);
    }

    setCopiedGeneratedPlotLink(true);

    copyTimeoutIdRef.current = setTimeout(() => {
      setCopiedGeneratedPlotLink(false);
      copyTimeoutIdRef.current = null;
    }, 2000);
  };
  return (
    <ActionModal header="Shareable plot link" isOpen={isOpen} onClose={onClose}>
      <ModalBody>
        <Flex direction="column" rowGap="1rem">
          <Stack spacing={1}>
            <Text fontSize="sm">Anyone with this link can view this plot</Text>
            <InputGroup size="md">
              <Input
                pr={checkData !== '' ? '6rem' : '7.5rem'}
                value={checkData === '' ? '' : checkData}
                placeholder="No shared link"
                disabled={checkData === ''}
              />
              <InputRightElement width={checkData !== '' ? '6rem' : '7.5rem'}>
                {checkData !== '' ? (
                  <CopyToClipboard
                    text={checkData}
                    onCopy={handleCopyGeneratedPlotLink}
                  >
                    <Button
                      h="1.75rem"
                      size="sm"
                      colorScheme="green"
                      variant="solid"
                    >
                      {copiedGeneratedPlotLink ? (
                        <>
                          <CheckIcon mr={1} /> Copied
                        </>
                      ) : (
                        'Copy Link'
                      )}
                    </Button>
                  </CopyToClipboard>
                ) : (
                  <Button
                    h="1.75rem"
                    size="sm"
                    colorScheme="green"
                    variant="solid"
                    isLoading={isLoading}
                    onClick={createPlotLink}
                  >
                    Generate Link
                  </Button>
                )}
              </InputRightElement>
            </InputGroup>
            {checkData !== '' && (
              <Box pt={1}>
                <Link
                  fontSize="sm"
                  textDecoration="underline"
                  _hover={{ textDecoration: 'none' }}
                  cursor="pointer"
                  href={checkData}
                  isExternal
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-flex' }}
                >
                  Preview link in new tab
                </Link>
              </Box>
            )}
          </Stack>
        </Flex>
      </ModalBody>
      <ModalFooter></ModalFooter>
    </ActionModal>
  );
}

export default PlotLinkModal;
